import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { Pagination } from '../types/common/Pagination';
import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import { buildPaginationParams } from '../client/queryParams';
import { buildOrderByParams } from '../client/queryParams';

/**
 * Base service for CRUD controllers.
 * Generic getAll, getById, create, update, delete with typed request/response.
 * AuthService does not inherit BaseService.
 */
export abstract class BaseService<
  TViewModel,
  TCreateModel,
  TUpdateModel,
  TListResult = TViewModel[],
> {
  constructor(
    protected readonly client: AxiosInstance,
    protected readonly basePath: string
  ) {}

  /**
   * GET list with optional pagination and orderBy.
   * Override to add filter params.
   */
  async getAll(params?: {
    pagination?: Pagination | null;
    orderBy?: OrderByDescriptor[] | null;
    [key: string]: unknown;
  }): Promise<ServiceResultT<TListResult>> {
    const query: Record<string, unknown> = { ...params };
    Object.assign(query, buildPaginationParams(params?.pagination ?? undefined));
    Object.assign(query, buildOrderByParams(params?.orderBy ?? undefined));
    delete query.pagination;
    delete query.orderBy;
    const { data } = await this.client.get<ServiceResultT<TListResult>>(this.basePath, { params: query });
    return data;
  }

  async getById(id: number | string): Promise<ServiceResultT<TViewModel>> {
    const { data } = await this.client.get<ServiceResultT<TViewModel>>(`${this.basePath}/${id}`);
    return data;
  }

  async create(payload: TCreateModel): Promise<ServiceResultT<TViewModel>> {
    const { data } = await this.client.post<ServiceResultT<TViewModel>>(this.basePath, payload);
    return data;
  }

  async update(id: number | string, payload: TUpdateModel): Promise<ServiceResultT<TViewModel>> {
    const { data } = await this.client.put<ServiceResultT<TViewModel>>(`${this.basePath}/${id}`, payload);
    return data;
  }

  async delete(id: number | string): Promise<import('../types/common').ServiceResult> {
    const { data } = await this.client.delete<import('../types/common').ServiceResult>(
      `${this.basePath}/${id}`
    );
    return data;
  }
}
